import React, { useState } from 'react';
import { ArrowRight, Building2, ClipboardCheck, PenLine, ShieldCheck, Info } from 'lucide-react';
import { PublicNavbar } from '../components/brand/PublicNavbar';
import { Footer } from '../components/brand/Footer';
import { InstitutionInquiryModal } from '../components/brand/InstitutionInquiryModal';
import { Seo } from '../components/seo/Seo';
import { organizationSchema, breadcrumbSchema } from '../lib/seoSchemas';

/* ───────────────────────────────────────────────────────────────────────
   Pilot Pricing — Fix-Now Part 2 §1 (Jul 2026).
   Replaces the retired clinician-pays fee-schedule page on /pricing.
   Honest-copy rule: no fee, percentage, tier, or expedited-payout offer
   appears anywhere on this page. One CTA → the existing pilot-request flow
   (InstitutionInquiryModal, interest="pilot"). The old FeeSchedules.js +
   PricingCalculator.js stay in the repo, de-routed, for when
   monetization surfaces are re-enabled.
   ─────────────────────────────────────────────────────────────────── */

const PILOT_INCLUDES = [
  {
    icon: ClipboardCheck,
    title: 'The discharge loop, end to end',
    body: 'Referral intake, automated matching, provider acceptance, and a committed first-visit window — one workspace for your planners.',
  },
  {
    icon: PenLine,
    title: 'Orders signed in one place',
    body: 'Placement paperwork routed and signed in-platform, so accepted referrals don\u2019t stall on a fax machine.',
  },
  {
    icon: ShieldCheck,
    title: 'Coverage checked before day one',
    body: 'Eligibility flags surface as exceptions your team clears before the first visit — during pilots, verification is labeled \u201csimulated (pilot)\u201d until live integrations are switched on.',
  },
];

export default function PilotPricingPage() {
  const [pilotOpen, setPilotOpen] = useState(false);

  return (
    <div className="min-h-screen bg-cn-bg text-cn-text" data-testid="pilot-pricing-page">
      <Seo
        title="Hospital Discharge Pilot Pricing | CaraLoom"
        description="CaraLoom is in hospital pilot. Pilot pricing is scoped per organization — based on discharge volume, integration needs, and pilot length. Talk to us and we'll scope it with you."
        path="/pricing"
        keywords={['hospital discharge pilot pricing', 'discharge coordination pilot', 'CaraLoom pilot']}
        schema={[
          organizationSchema(),
          breadcrumbSchema([{ name: 'Home', path: '/' }, { name: 'Pricing', path: '/pricing' }]),
        ]}
      />
      <PublicNavbar />

      {/* Hero */}
      <section className="relative overflow-hidden border-b border-cn-border">
        <div className="cn-container pt-16 md:pt-24 pb-16 md:pb-20">
          <div className="max-w-3xl">
            <div className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full bg-cn-bone-200/80 dark:bg-white/5 border border-cn-border mb-6">
              <Building2 className="w-3.5 h-3.5 text-cn-primary" aria-hidden="true" />
              <span className="text-xs font-semibold tracking-wide uppercase text-cn-text">Pricing</span>
            </div>
            <h1 className="cn-display text-[2.6rem] sm:text-5xl lg:text-6xl leading-[1.04] tracking-tight"
                data-testid="pricing-hero-headline">
              CaraLoom is in{' '}
              <span className="italic font-medium text-cn-primary dark:text-cn-accent">hospital pilot.</span>
            </h1>
            <p className="mt-6 text-lg md:text-xl text-cn-text-muted leading-relaxed max-w-2xl"
                data-testid="pricing-pilot-copy">
              Pricing is scoped per pilot — shaped by your discharge volume, integration
              needs, and pilot length. There is no public fee schedule while we&apos;re in
              pilot. Talk to us and we&apos;ll scope it with you.
            </p>
            <div className="mt-9">
              <button
                type="button"
                onClick={() => setPilotOpen(true)}
                className="cn-btn cn-btn-primary text-base font-bold"
                data-testid="pricing-cta-pilot"
              >
                Talk to us about a pilot <ArrowRight className="w-4 h-4" />
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* What a pilot includes */}
      <section className="py-20 md:py-24 bg-cn-bone-100 dark:bg-cn-ink-900 border-b border-cn-border"
          data-testid="pricing-pilot-includes">
        <div className="cn-container">
          <div className="max-w-2xl mb-12">
            <div className="cn-eyebrow mb-3">What a pilot includes</div>
            <h2 className="cn-display text-4xl md:text-5xl leading-[1.05] tracking-tight">
              Everything the loop needs. Nothing it doesn&apos;t.
            </h2>
          </div>
          <div className="grid md:grid-cols-3 gap-6">
            {PILOT_INCLUDES.map((t) => (
              <div key={t.title} className="cn-card p-8 bg-white dark:bg-cn-ink-800">
                <span className="w-12 h-12 rounded-2xl bg-cn-sage-100 flex items-center justify-center">
                  <t.icon className="w-6 h-6 text-cn-primary" />
                </span>
                <h3 className="mt-5 font-display font-semibold text-xl text-cn-text">{t.title}</h3>
                <p className="mt-2.5 text-cn-text-muted leading-relaxed">{t.body}</p>
              </div>
            ))}
          </div>
          <div className="mt-10 flex items-start gap-2 text-xs text-cn-text-muted max-w-3xl"
              data-testid="pricing-honesty-note">
            <Info className="w-3.5 h-3.5 shrink-0 mt-0.5" />
            Honest by design: pilot scope, timeline, and pricing are agreed in writing
            before anything goes live. Integrations still in test mode stay clearly
            labeled inside the product.
          </div>
        </div>
      </section>

      <InstitutionInquiryModal
        open={pilotOpen}
        onOpenChange={setPilotOpen}
        defaultInterest="pilot"
        source="pricing_pilot_page"
      />

      <Footer />
    </div>
  );
}
